import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  CircularProgress,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Link as MuiLink,
  Divider,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorIcon from '@mui/icons-material/Error';
import ImageIcon from '@mui/icons-material/Image';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import api from '../../config/api';
import OtherToolsSection from '../OtherToolsSection';

const faqs = [
  {
    q: 'What is a DMARC record?',
    a: 'A DMARC record is a DNS record that helps prevent email spoofing by specifying how to handle emails that fail SPF and DKIM checks.'
  },
  {
    q: 'Why does my DMARC record fail?',
    a: 'Common reasons include syntax errors, missing SPF or DKIM records, or incorrect policy settings.'
  },
  {
    q: 'How do I fix DMARC issues?',
    a: 'Update your DNS with the correct DMARC record, ensure SPF and DKIM are properly configured, and set an appropriate policy.'
  },
  {
    q: 'What is the difference between p=none, p=quarantine, and p=reject?',
    a: 'These are DMARC policy actions: none (monitor only), quarantine (treat as suspicious), reject (block non-compliant emails).'
  },
  {
    q: 'How do I receive DMARC reports?',
    a: 'Add the rua and ruf tags to your DMARC record with mailto: addresses to receive aggregate and forensic reports.'
  },
  {
    q: 'Can I have multiple DMARC records?',
    a: 'No, a domain must have only one DMARC record. Multiple records will cause DMARC to fail.'
  },
  {
    q: 'What is alignment in DMARC?',
    a: 'Alignment means the domain in the From header matches (or is a subdomain of) the domain in SPF and DKIM.'
  },
  {
    q: 'How do I test my DMARC record?',
    a: 'Use online tools like this DMARC Analyzer or command-line tools (dig, nslookup) to check and validate your DMARC record.'
  },
  {
    q: 'What happens if my DMARC record is too long?',
    a: 'DMARC records longer than 255 characters must be split into multiple strings in the DNS. If not, the record may be ignored.'
  },
  {
    q: 'What is a DMARC failure?',
    a: 'A DMARC failure occurs when an email fails both SPF and DKIM checks, and the policy is set to quarantine or reject.'
  }
];

interface DMARCCheck {
  label: string;
  status: string;
  details: string | null;
}

interface DMARCResult {
  domain: string;
  timestamp: string;
  dmarc: string;
  checks: DMARCCheck[];
}

const DMARCAnalyzer: React.FC = () => {
  const [domain, setDomain] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<DMARCResult | null>(null);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const resultRef = React.useRef<HTMLDivElement>(null);

  const validateDomain = (d: string) => /^([a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/.test(d);

  const analyzeDMARCTags = (dmarcRecord: string): DMARCCheck[] => {
    const checks: DMARCCheck[] = [];
    
    // Policy check
    const policyMatch = dmarcRecord.match(/p=([a-z]+)/i);
    const policy = policyMatch ? policyMatch[1].toLowerCase() : null;
    checks.push({
      label: 'Policy',
      status: policy ? `Pass (Policy set to ${policy})` : 'Warning (No policy specified)',
      details: policy ? {
        none: 'Monitor only - no action taken',
        quarantine: 'Treat as suspicious - usually goes to spam',
        reject: 'Block non-compliant emails'
      }[policy] || null : null
    });

    // Reporting checks
    const hasRua = dmarcRecord.includes('rua=');
    const hasRuf = dmarcRecord.includes('ruf=');
    checks.push({
      label: 'Reporting',
      status: hasRua ? 'Pass' : 'Warning (No aggregate reports configured)',
      details: hasRua ? 'Aggregate reports (rua) configured' : 'Add rua=mailto:address to receive aggregate reports'
    });
    checks.push({
      label: 'Forensic Reports',
      status: hasRuf ? 'Pass' : 'Warning (No forensic reports configured)',
      details: hasRuf ? 'Forensic reports (ruf) configured' : 'Add ruf=mailto:address to receive detailed failure reports'
    });

    // Failure options check
    const hasFo = dmarcRecord.includes('fo=');
    checks.push({
      label: 'Failure Options',
      status: hasFo ? 'Pass' : 'Warning (No failure options specified)',
      details: hasFo ? 'Failure options configured' : 'Add fo=0:1:d:s to receive detailed failure reports'
    });

    // Percentage check
    const hasPct = dmarcRecord.includes('pct=');
    checks.push({
      label: 'Percentage',
      status: hasPct ? 'Pass' : 'Warning (No percentage specified)',
      details: hasPct ? 'Percentage of messages to apply policy to' : 'Add pct=100 to apply policy to all messages'
    });

    // Alignment checks
    const hasAdkim = dmarcRecord.includes('adkim=');
    const hasAspf = dmarcRecord.includes('aspf=');
    checks.push({
      label: 'DKIM Alignment',
      status: hasAdkim ? 'Pass' : 'Warning (No DKIM alignment specified)',
      details: hasAdkim ? 'DKIM alignment mode configured' : 'Add adkim=s for strict DKIM alignment'
    });
    checks.push({
      label: 'SPF Alignment',
      status: hasAspf ? 'Pass' : 'Warning (No SPF alignment specified)',
      details: hasAspf ? 'SPF alignment mode configured' : 'Add aspf=s for strict SPF alignment'
    });

    return checks;
  };

  const handleAnalyze = async () => {
    setError('');
    setResult(null);
    if (!domain) {
      setError('Please enter a domain.');
      return;
    }
    if (!validateDomain(domain)) {
      setError('Please enter a valid domain.');
      return;
    }
    setLoading(true);
    try {
      const response = await api.get(`/api/dns/check/DMARC?domain=${encodeURIComponent(domain)}`);
      if (!response.data || !response.data.dmarc) {
        setError('No DMARC record found for this domain.');
        setResult(null);
      } else {
        const resultWithChecks = {
          ...response.data,
          checks: analyzeDMARCTags(response.data.dmarc)
        };
        setResult(resultWithChecks);
      }
    } catch (err: any) {
      console.error('DMARC analysis error:', err);
      setError(err.response?.data?.error || 'Failed to fetch DMARC data. Please try again.');
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!result || !result.dmarc || !result.checks) return;
    const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let y = pageHeight / 2 - 60;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(32);
    doc.setTextColor('#0B60B1');
    doc.text('DMARC AUDIT REPORT', 60, y);
    y += 40;
    doc.setFontSize(18);
    doc.setTextColor('#222');
    doc.text(`Domain: ${domain}`, 60, y);
    y += 28;
    doc.setFontSize(12);
    doc.setTextColor('#888');
    const now = new Date();
    doc.text(`Date: ${now.toLocaleDateString()}   Year: ${now.getFullYear()}`, 60, y);
    y += 22;
    doc.setFontSize(10);
    doc.setTextColor('#888');
    doc.text('Generated by: QuillBee – Deliverability Agent for ImpactQuill', 60, y);
    doc.addPage();
    // Header for page 2
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.setTextColor('#0B60B1');
    doc.text(`DMARC report for ${domain}`, 40, 110);
    let y2 = 140;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor('#0B60B1');
    doc.text('DMARC Record:', 40, y2);
    y2 += 18;
    doc.setFont('courier', 'normal');
    doc.setFontSize(11);
    doc.setTextColor('#222');
    const dmarcLines = doc.splitTextToSize(result.dmarc, pageWidth - 80);
    dmarcLines.forEach((line: string) => {
      doc.text(line, 40, y2);
      y2 += 16;
    });
    y2 += 10;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor(result.dmarc ? '#388e3c' : '#d32f2f');
    doc.text(`Status: ${result.dmarc ? 'PASS' : 'FAIL'}`, 40, y2);
    y2 += 24;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor('#0B60B1');
    doc.text('DMARC Checks:', 40, y2);
    y2 += 18;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(12);
    doc.setTextColor('#222');
    result.checks.forEach((check: DMARCCheck) => {
      doc.text(`• ${check.label}: ${check.status}`, 60, y2);
      y2 += 18;
      if (check.details) {
        doc.text(`  ${check.details}`, 60, y2);
        y2 += 18;
      }
    });
    doc.save(`dmarc-analysis-${domain}.pdf`);
  };

  const handleDownloadJPG = async () => {
    if (!resultRef.current) return;
    const canvas = await html2canvas(resultRef.current, { backgroundColor: '#fff', scale: 2 });
    const link = document.createElement('a');
    link.download = `${domain}_dmarc_result.jpg`;
    link.href = canvas.toDataURL('image/jpeg', 0.95);
    link.click();
  };

  const getDMARCSummary = (dmarcRecord: string) => {
    const tags: Record<string, string | null> = {
      v: null, p: null, rua: null, ruf: null, fo: null, pct: null, adkim: null, aspf: null
    };
    dmarcRecord.split(';').forEach(part => {
      const [k, v] = part.trim().split('=');
      if (k && v && tags.hasOwnProperty(k)) tags[k] = v;
    });
    return {
      valid: !!tags.v && !!tags.p,
      policy: tags.p,
      rua: tags.rua,
      ruf: tags.ruf
    };
  };

  return (
    <Box sx={{ bgcolor: '#f7fafd', minHeight: '100vh', py: 0 }}>
      {/* Hero Banner */}
      <Box sx={{ bgcolor: '#0B60B1', color: '#fff', py: 6, textAlign: 'center' }}>
        <Typography variant="h3" fontWeight={700} gutterBottom>DMARC Analyzer</Typography>
        <Typography variant="h6" maxWidth={600} mx="auto">
          Analyze and validate your DMARC records for best practices.
        </Typography>
      </Box>

      {/* Input Section */}
      <Box sx={{ py: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Typography variant="subtitle1" mb={2}>Enter your domain to check DMARC:</Typography>
        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, width: '100%', maxWidth: 400 }}>
          <TextField
            label="Domain"
            variant="outlined"
            value={domain}
            onChange={e => setDomain(e.target.value)}
            fullWidth
            disabled={loading}
            error={!!error}
            helperText={error}
            onKeyDown={e => {
              if (e.key === 'Enter' && !loading && domain) handleAnalyze();
            }}
          />
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleAnalyze} 
            disabled={loading || !domain} 
            sx={{ 
              minWidth: 120,
              height: 56, // Match TextField height
              '& .MuiCircularProgress-root': {
                color: 'white'
              }
            }}
          >
            {loading ? <CircularProgress size={24} /> : 'Analyze'}
          </Button>
        </Box>
        {loading && <CircularProgress sx={{ mt: 3 }} />}
        {error && <Typography color="error" sx={{ mt: 2 }}>{error}</Typography>}
      </Box>

      {/* Result Section */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      )}
      {result && result.dmarc && (
        <Card ref={resultRef} sx={{ width: '100%', maxWidth: 900, mx: 'auto', mb: 4, p: 2, boxShadow: 6, borderRadius: 3, bgcolor: '#fff' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <MuiLink href={`https://${domain}`} target="_blank" rel="noopener noreferrer" underline="hover" variant="subtitle1" sx={{ fontWeight: 600, fontSize: 18 }}>
                  {domain}
                </MuiLink>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Tooltip title={copied ? 'Copied!' : 'Copy DMARC Record'}>
                  <IconButton onClick={() => { navigator.clipboard.writeText(result.dmarc); setCopied(true); setTimeout(() => setCopied(false), 1200); }}>
                    <ContentCopyIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Download PDF">
                  <IconButton onClick={handleDownloadPDF}>
                    <PictureAsPdfIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Download as JPG">
                  <IconButton onClick={handleDownloadJPG}>
                    <ImageIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ mb: 2 }}>
              <Chip
                icon={<CheckCircleIcon />}
                label={result.dmarc ? 'PASS' : 'FAIL'}
                color={result.dmarc ? 'success' : 'error'}
                sx={{ 
                  fontWeight: 700, 
                  fontSize: 16, 
                  px: 2, 
                  py: 1, 
                  mb: 1,
                  '& .MuiChip-icon': {
                    color: 'inherit'
                  }
                }}
              />
            </Box>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>DMARC Record</Typography>
            <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f7fafd', mb: 2 }}>
              <Typography variant="body1" component="pre" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', fontFamily: 'monospace', fontSize: 16 }}>
                {result.dmarc}
              </Typography>
            </Paper>
            <Divider sx={{ my: 2 }} />
            {result && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>DMARC Analysis</Typography>
                <Box component="table" sx={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, bgcolor: '#f7fafd', borderRadius: 2, overflow: 'hidden', boxShadow: 1 }}>
                  <tbody>
                    {Array.isArray(result.checks) && result.checks.length > 0 && result.checks.map((check) => {
                      let tagValue = null;
                      if (result.dmarc) {
                        const match = result.dmarc.match(new RegExp(check.label.toLowerCase().replace(/ /g, '') + '=([^;\s]+)', 'i'));
                        if (match) tagValue = match[1];
                      }
                      return (
                        <tr key={check.label}>
                          <td style={{ verticalAlign: 'top', padding: '12px 8px 12px 16px', width: 40, borderBottom: '1px solid #e0e0e0' }}>
                            {check.status.includes('Pass') ? (
                              <CheckCircleIcon sx={{ color: '#2e7d32', fontSize: 22, mt: 0.5 }} />
                            ) : check.status.includes('Warning') ? (
                              <WarningAmberIcon sx={{ color: '#ed6c02', fontSize: 22, mt: 0.5 }} />
                            ) : (
                              <ErrorIcon sx={{ color: '#d32f2f', fontSize: 22, mt: 0.5 }} />
                            )}
                          </td>
                          <td style={{ verticalAlign: 'top', padding: '12px 8px', fontWeight: 700, width: '180px', borderBottom: '1px solid #e0e0e0' }}>
                            {check.label}
                          </td>
                          <td style={{ verticalAlign: 'top', padding: '12px 16px', borderBottom: '1px solid #e0e0e0' }}>
                            {check.details}
                            {check.status.includes('Pass') && check.details ? (
                              <span style={{ color: '#2e7d32', fontWeight: 500, marginLeft: 8 }}>
                                {tagValue ? ` (${check.label.split(' ')[0].toLowerCase()}=${tagValue})` : ''}
                              </span>
                            ) : null}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Box>
              </Box>
            )}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>Tag Breakdown</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
                {Array.isArray(result.checks) && result.checks.length > 0 && result.checks.map((check) => (
                  <Paper key={check.label} elevation={2} sx={{ p: 2, display: 'flex', alignItems: 'flex-start', gap: 2, height: '100%' }}>
                    {check.status.includes('Pass') ? (
                      <CheckCircleIcon sx={{ color: '#2e7d32', fontSize: 28, mt: 0.5 }} />
                    ) : check.status.includes('Warning') ? (
                      <WarningAmberIcon sx={{ color: '#ed6c02', fontSize: 28, mt: 0.5 }} />
                    ) : (
                      <ErrorIcon sx={{ color: '#d32f2f', fontSize: 28, mt: 0.5 }} />
                    )}
                    <Box>
                      <Typography fontWeight={700}>{check.label}</Typography>
                      <Typography variant="body2" color="text.secondary">{check.details}</Typography>
                    </Box>
                  </Paper>
                ))}
              </Box>
            </Box>
            {/* DMARC Summary */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>DMARC Summary</Typography>
              {(() => {
                const summary = getDMARCSummary(result.dmarc);
                return (
                  <Box component="table" sx={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, mb: 2 }}>
                    <tbody>
                      <tr>
                        <td><Typography fontWeight={600}>Valid DMARC record</Typography></td>
                        <td>
                          {summary.valid ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', color: 'success.main', gap: 1 }}>
                              <CheckCircleIcon fontSize="small" /> Yes
                            </Box>
                          ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', color: 'error.main', gap: 1 }}>
                              <ErrorIcon fontSize="small" /> No
                            </Box>
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td><Typography fontWeight={600}>DMARC policy</Typography></td>
                        <td>
                          {summary.policy ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: summary.policy === 'reject' ? 'success.main' : summary.policy === 'quarantine' ? 'warning.main' : 'error.main' }}>
                              {summary.policy === 'reject' ? <CheckCircleIcon fontSize="small" /> : summary.policy === 'quarantine' ? <WarningAmberIcon fontSize="small" /> : <ErrorIcon fontSize="small" />} 
                              {summary.policy}
                            </Box>
                          ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', color: 'error.main', gap: 1 }}>
                              <ErrorIcon fontSize="small" /> Not specified
                            </Box>
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td><Typography fontWeight={600}>Aggregate Report (RUA) addresses</Typography></td>
                        <td>
                          {summary.rua ? (
                            <Box sx={{ color: 'success.main' }}>{summary.rua}</Box>
                          ) : (
                            <Box sx={{ color: 'warning.main' }}>Not specified</Box>
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td><Typography fontWeight={600}>Forensic Report (RUF) addresses</Typography></td>
                        <td>
                          {summary.ruf ? (
                            <Box sx={{ color: 'success.main' }}>{summary.ruf}</Box>
                          ) : (
                            <Box sx={{ color: 'warning.main' }}>Not specified</Box>
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </Box>
                );
              })()}
            </Box>
            {/* Errors / Recommendations */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>Errors / Recommendations</Typography>
              <Box>
                {Array.isArray(result.checks) && result.checks.filter(c => c.status.includes('Warning') || c.status.includes('No')).length > 0 ? (
                  result.checks.filter(c => c.status.includes('Warning') || c.status.includes('No')).map((check) => (
                    <Box key={check.label} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <WarningAmberIcon sx={{ color: '#ed6c02', fontSize: 20 }} />
                      <Typography variant="body2" color="warning.main">
                        {check.label}: {check.details || check.status}
                      </Typography>
                    </Box>
                  ))
                ) : (
                  <Typography variant="body2" color="success.main">No major issues detected.</Typography>
                )}
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* FAQ Section */}
      <Container maxWidth="md" sx={{ mt: 6, mb: 8 }}>
        <Paper sx={{ p: 4, borderRadius: 3, boxShadow: 2 }}>
          <Typography variant="h5" fontWeight={700} textAlign="center" mb={3}>DMARC FAQ</Typography>
          {faqs.map((faq) => (
            <Accordion key={faq.q} sx={{ mb: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" fontWeight={600}>{faq.q}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary">{faq.a}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Paper>
      </Container>

      <OtherToolsSection current="DMARC Analyzer" />

      {/* CTA Section: Need help with email deliverability? */}
      <Box sx={{ py: 6, textAlign: 'center', bgcolor: '#e3f0fc', mt: 8 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" fontWeight={700} mb={1} color="primary">
            Need help with email deliverability?
          </Typography>
          <Typography variant="body1" color="text.secondary" mb={3} maxWidth={420}>
            Our experts can help you improve your inbox placement and resolve deliverability issues. Get personalized solutions for your email infrastructure.
          </Typography>
          <Button variant="contained" color="primary" size="large" href="/contact" sx={{ boxShadow: 2, px: 4, py: 1.5, fontWeight: 700 }}>
            Contact Our Experts
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default DMARCAnalyzer; 